import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Tooltip,
  Paper,
  Snackbar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Edit as EditIcon,
  Visibility as PreviewIcon,
  PictureAsPdf as PdfIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  MenuBook as ManualIcon,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import api from '../../services/api';

import useManualStore from './useManualStore';
import ManualEditor from './ManualEditor';
import ManualChapterView from './ManualChapterView';
import ManualTableOfContents from './ManualTableOfContents';
import { MP } from './manualPalette';
export { MP } from './manualPalette';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
];

// ── Util: fetch an img URL and return base64 data URL via canvas ─────────────
const toBase64 = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      try {
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src.startsWith('data:') ? src : src + '?t=' + Date.now();
  });

const UserManual = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pdfLang, setPdfLang] = useState('en');
  const [generating, setGenerating] = useState(false);
  const [pdfStatus, setPdfStatus] = useState('');   // progress label shown during generation
  const [pdfError, setPdfError] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [activeChapter, setActiveChapter] = useState('ch1');
  const previewRef = useRef(null);

  const {
    content,
    imageOverrides,
    updateMeta,
    updateSection,
    updateStepItem,
    addStep,
    removeStep,
    moveStep,
    updateImage,
    removeImageOverride,
    resetChapter,
    resetAll,
    saveNow,
  } = useManualStore();

  useEffect(() => {
    if (activeTab !== 1) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveChapter(e.target.id.replace('chapter-', ''));
        });
      },
      { threshold: 0.15 }
    );
    const els = document.querySelectorAll('[id^="chapter-"]');
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeTab]);

  const handleSave = useCallback(() => {
    saveNow();
    setSnackMsg('Manual saved!');
    setSnackOpen(true);
  }, [saveNow]);

  const handleResetAll = useCallback(() => {
    if (window.confirm('Reset ALL chapters to default? This cannot be undone.')) {
      resetAll();
      setSnackMsg('Manual reset to defaults.');
      setSnackOpen(true);
    }
  }, [resetAll]);

  // ── PDF generation ──────────────────────────────────────────────────────────
  const handleGeneratePDF = useCallback(async () => {
    setGenerating(true);
    setPdfError(null);
    setPdfStatus('');

    try {
      // Step 1: Translate content if needed
      let pdfContent = content;
      if (pdfLang !== 'en') {
        const langLabel = LANGUAGES.find(l => l.code === pdfLang)?.label;
        setPdfStatus(`Translating to ${langLabel}…`);
        try {
          const resp = await api.post('/manual/translate', { content, targetLang: pdfLang }, { timeout: 120000 });
          if (resp.data?.translated) {
            pdfContent = resp.data.translated;
            setPdfStatus(`Translated to ${langLabel} ✓`);
          } else {
            console.warn('Translation response missing translated field:', resp.data);
            setPdfStatus('Translation returned empty — using English');
            await new Promise(r => setTimeout(r, 1200));
          }
        } catch (transErr) {
          const msg = transErr.response?.data?.error || transErr.message;
          console.error('Translation API error:', msg);
          setPdfError(`Translation failed: ${msg}. PDF generated in English.`);
          await new Promise(r => setTimeout(r, 1200));
        }
      }

      // Step 2: Pre-load all images as base64 so they embed in the PDF
      setPdfStatus('Loading images…');
      const imageCache = {};
      const allSections = pdfContent.chapters.flatMap((ch) => ch.sections);
      await Promise.all(
        allSections.map(async (sec) => {
          const imgData = imageOverrides[sec.id] || sec.image;
          if (!imgData?.src) return;
          const b64 = await toBase64(imgData.src);
          if (b64) imageCache[sec.id] = b64;
        })
      );

      setPdfStatus('Building PDF…');

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = 210;
      const pageH = 297;
      const margin = 18;
      const contentW = pageW - margin * 2;
      let y = margin;

      // Brand RGB
      const DEEP  = [13, 27, 75];    // deepBlue
      const GREEN = [27, 94, 32];    // green
      const GOLD  = [249, 168, 37];  // yellow
      const BLACK = [10, 10, 10];

      const newPage = () => {
        doc.addPage();
        y = margin;
        doc.setFillColor(...DEEP);
        doc.rect(0, 0, pageW, 7, 'F');
        doc.setFillColor(...GOLD);
        doc.rect(0, 7, pageW, 1.5, 'F');
        y = 16;
      };

      const checkY = (needed = 10) => { if (y + needed > pageH - 18) newPage(); };

      const sanitize = (s) => String(s)
        .replace(/[\u{1F000}-\u{1FFFF}]/gu, '')
        .replace(/[\u{2600}-\u{27BF}]/gu, '')
        .replace(/[\u{FE00}-\u{FE0F}]/gu, '')
        .replace(/[\u{200D}]/gu, '')
        .replace(/\s{2,}/g, ' ').trim();

      const writeText = (text, size, bold, rgb = BLACK, indent = 0) => {
        if (!text) return;
        const clean = sanitize(text);
        if (!clean) return;
        doc.setFontSize(size);
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setTextColor(rgb[0], rgb[1], rgb[2]);
        const lines = doc.splitTextToSize(clean, contentW - indent);
        checkY(lines.length * (size * 0.42) + 3);
        doc.text(lines, margin + indent, y);
        y += lines.length * (size * 0.42) + 2;
      };

      const writeCallout = (text, type) => {
        if (!text) return;
        const clean = sanitize(text);
        if (!clean) return;
        const configs = {
          tip:     { bg: [232, 245, 233], stripe: GREEN },
          note:    { bg: [227, 232, 255], stripe: DEEP  },
          warning: { bg: [255, 248, 225], stripe: GOLD  },
        };
        const c = configs[type] || configs.note;

        const textX = margin + 14;
        const textW = contentW - 14 - 2;
        const lines = doc.splitTextToSize(clean, textW);
        const lineH = 4.5;
        const padV = 4.5;
        const h = Math.max(lines.length * lineH + padV * 2, 12);
        checkY(h + 4);

        // Outer rounded box
        doc.setFillColor(...c.bg);
        doc.setDrawColor(...c.stripe);
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, y, contentW, h, 2, 2, 'FD');

        // Left accent stripe — inset 2 mm top/bottom to clear rounded corners
        doc.setFillColor(...c.stripe);
        doc.rect(margin, y + 2, 3, h - 4, 'F');

        // ── Icon, vertically centred in the box ─────────────────────────
        const iconX  = margin + 8.5;
        const iconCY = y + h / 2;

        if (type === 'tip') {
          // Lightbulb: filled circle (bulb) + two base rects (socket)
          doc.setFillColor(...GREEN);
          doc.circle(iconX, iconCY - 1.2, 2.8, 'F');
          doc.rect(iconX - 1.3, iconCY + 1.6, 2.6, 1.0, 'F');
          doc.rect(iconX - 1.6, iconCY + 2.6, 3.2, 0.5, 'F');

        } else if (type === 'warning') {
          // Filled equilateral triangle
          const r = 3.5;
          doc.setFillColor(...GOLD);
          doc.lines([[r * 0.866, r * 1.5], [-r * 1.732, 0]], iconX, iconCY - r, [1, 1], 'F', true);
          // "!" centred inside
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(255, 255, 255);
          const exW = doc.getTextWidth('!');
          doc.text('!', iconX - exW / 2, iconCY + 1.8);

        } else {
          // Note: filled circle + "i"
          doc.setFillColor(...DEEP);
          doc.circle(iconX, iconCY, 3, 'F');
          doc.setFontSize(7);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(255, 255, 255);
          const iW = doc.getTextWidth('i');
          doc.text('i', iconX - iW / 2, iconCY + 2.3);
        }

        // ── Body text, vertically centred ───────────────────────────────
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(25, 25, 25);
        const textBlockH = lines.length * lineH;
        const textY = y + (h - textBlockH) / 2 + lineH - 1;
        doc.text(lines, textX, textY);

        y += h + 4;
      };

      // ── COVER PAGE ──────────────────────────────────────────────────────────
      doc.setFillColor(...DEEP);
      doc.rect(0, 0, pageW, pageH, 'F');

      // Gold accent bar
      doc.setFillColor(...GOLD);
      doc.rect(0, pageH * 0.55, pageW, 3, 'F');

      // Green side stripe
      doc.setFillColor(...GREEN);
      doc.rect(0, 0, 6, pageH, 'F');

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(249, 168, 37);
      doc.text('SOLDIKEEPER', 18, 40);

      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      const titleLines = doc.splitTextToSize(pdfContent.meta.title || 'User Manual', contentW);
      doc.text(titleLines, 18, 54);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(180, 200, 255);
      const subLines = doc.splitTextToSize(pdfContent.meta.subtitle || '', contentW - 10);
      doc.text(subLines, 18, 54 + titleLines.length * 12 + 4);

      doc.setFontSize(9);
      doc.setTextColor(249, 168, 37);
      doc.text(`Version ${pdfContent.meta.version || '2.6.0'}  ·  ${pdfContent.meta.lastUpdated || ''}`, 18, pageH - 28);
      doc.setTextColor(160, 180, 220);
      doc.text(`Language: ${LANGUAGES.find((l) => l.code === pdfLang)?.label || 'English'}`, 18, pageH - 22);

      // ── TABLE OF CONTENTS ───────────────────────────────────────────────────
      newPage();
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...DEEP);
      doc.text('Table of Contents', margin, y);
      y += 2;
      doc.setFillColor(...GOLD);
      doc.rect(margin, y, 40, 1, 'F');
      y += 7;

      pdfContent.chapters.forEach((ch, idx) => {
        checkY(7);
        doc.setFontSize(9.5);
        doc.setFont('helvetica', idx < 12 ? 'normal' : 'italic');
        doc.setTextColor(...BLACK);
        doc.setFillColor(...DEEP);
        doc.roundedRect(margin, y - 3.5, 8, 5, 1, 1, 'F');
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(String(idx + 1), margin + 4, y, { align: 'center' });
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...BLACK);
        doc.text(ch.title, margin + 12, y);
        // Dot leader
        doc.setTextColor(180, 180, 180);
        const titleW = doc.getTextWidth(ch.title);
        const dotsStart = margin + 12 + titleW + 2;
        const dotsEnd = pageW - margin - 12;
        let dx = dotsStart;
        doc.setFontSize(7);
        while (dx < dotsEnd) { doc.text('.', dx, y); dx += 2.5; }
        y += 6.5;
      });

      // ── CHAPTERS ────────────────────────────────────────────────────────────
      pdfContent.chapters.forEach((chapter, chIdx) => {
        newPage();

        // Chapter header
        doc.setFillColor(...DEEP);
        doc.rect(margin - 2, y - 5, contentW + 4, 14, 'F');
        doc.setFillColor(...GOLD);
        doc.rect(margin - 2, y + 9, contentW + 4, 1.2, 'F');

        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(`${chIdx + 1}.  ${chapter.title}`, margin + 2, y + 5);
        y += 20;

        chapter.sections.forEach((section, sIdx) => {
          const imgB64 = imageCache[section.id];
          const imgCaption = (imageOverrides[section.id] || section.image)?.caption;

          checkY(14);

          // Section heading bar
          doc.setFillColor(232, 235, 255);
          doc.rect(margin, y - 3, contentW, 8, 'F');
          doc.setFillColor(...GREEN);
          doc.rect(margin, y - 3, 3, 8, 'F');
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...DEEP);
          doc.text(`${chIdx + 1}.${sIdx + 1}  ${section.title}`, margin + 6, y + 2.5);
          y += 10;

          if (section.body) writeText(section.body, 9.5, false, BLACK);

          if (section.steps) {
            section.steps.forEach((step, i) => {
              const cleanStep = sanitize(step);
              if (!cleanStep) return;
              const sLines = doc.splitTextToSize(cleanStep, contentW - 14);
              checkY(sLines.length * 4.8 + 3);
              doc.setFillColor(...GREEN);
              doc.circle(margin + 3.5, y - 0.5, 3, 'F');
              doc.setFontSize(7.5);
              doc.setFont('helvetica', 'bold');
              doc.setTextColor(255, 255, 255);
              const numStr = String(i + 1);
              doc.text(numStr, margin + 3.5 - doc.getTextWidth(numStr) / 2, y);
              doc.setFontSize(9.5);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(...BLACK);
              doc.text(sLines, margin + 10, y);
              y += sLines.length * 4.8 + 1.5;
            });
            y += 2;
          }

          if (section.items) {
            section.items.forEach((item) => {
              const rawText = typeof item === 'object' ? `${item.issue}: ${item.solution}` : item;
              const cleanItem = sanitize(rawText);
              if (!cleanItem) return;
              const iLines = doc.splitTextToSize(cleanItem, contentW - 12);
              checkY(iLines.length * 4.8 + 3);
              doc.setFillColor(...GOLD);
              doc.circle(margin + 3, y - 1, 1.5, 'F');
              doc.setFontSize(9.5);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(...BLACK);
              doc.text(iLines, margin + 8, y);
              y += iLines.length * 4.8 + 1.5;
            });
            y += 2;
          }

          if (section.tip)     writeCallout(section.tip, 'tip');
          if (section.note)    writeCallout(section.note, 'note');
          if (section.warning) writeCallout(section.warning, 'warning');

          // Embed image
          if (imgB64) {
            try {
              const maxW = contentW * 0.72;
              const maxH = 58;
              checkY(maxH + 8);
              doc.setFontSize(7.5);
              doc.setFont('helvetica', 'italic');
              doc.setTextColor(100, 100, 100);
              if (imgCaption) doc.text(`▲ ${imgCaption}`, margin, y + maxH + 4);
              doc.addImage(imgB64, 'JPEG', margin, y, maxW, maxH, undefined, 'MEDIUM');
              y += maxH + (imgCaption ? 8 : 4);
            } catch (imgErr) {
              // silently skip broken images
            }
          }

          y += 3;
        });
      });

      // ── PAGE NUMBERS ────────────────────────────────────────────────────────
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFillColor(...DEEP);
        doc.rect(0, pageH - 10, pageW, 10, 'F');
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(160, 180, 220);
        doc.text(
          `SoldiKeeper User Manual v${pdfContent.meta.version || '2.6.0'}  ·  Page ${i} of ${pageCount}`,
          pageW / 2, pageH - 4, { align: 'center' }
        );
      }

      const langLabel = LANGUAGES.find((l) => l.code === pdfLang)?.label || 'EN';
      doc.save(`SoldiKeeper_Manual_${langLabel}.pdf`);
      setSnackMsg(`✅ PDF saved: SoldiKeeper_Manual_${langLabel}.pdf`);
      setSnackOpen(true);
    } catch (err) {
      console.error('PDF error:', err);
      setPdfError('PDF generation failed: ' + err.message);
    } finally {
      setGenerating(false);
      setPdfStatus('');
    }
  }, [content, imageOverrides, pdfLang]);

  const actions = {
    updateMeta, updateSection, updateStepItem,
    addStep, removeStep, moveStep,
    updateImage, removeImageOverride, resetChapter,
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: MP.deepBlue }}>
      {/* ── Top toolbar ──────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: MP.black,
          borderBottom: `3px solid ${MP.yellow}`,
          px: { xs: 2, md: 4 },
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Brand */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
          <ManualIcon sx={{ color: MP.yellow }} />
          <Typography variant="h6" fontWeight={800} sx={{ color: MP.white, display: { xs: 'none', sm: 'block' } }}>
            User Manual
          </Typography>
          <Chip
            label={`v${content.meta.version}`}
            size="small"
            sx={{ bgcolor: MP.green, color: MP.white, fontWeight: 700, fontSize: '0.65rem', height: 20 }}
          />
        </Box>

        {/* Edit / Preview tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, py: 0, color: MP.textMuted, fontWeight: 600 },
            '& .Mui-selected': { color: `${MP.yellow} !important` },
            '& .MuiTabs-indicator': { bgcolor: MP.yellow, height: 3 },
          }}
        >
          <Tab icon={<EditIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Edit" />
          <Tab icon={<PreviewIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Preview" />
        </Tabs>

        <Box sx={{ flex: 1 }} />

        {/* Actions */}
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel sx={{ fontSize: '0.8rem', color: MP.textMuted }}>PDF Language</InputLabel>
            <Select
              value={pdfLang}
              label="PDF Language"
              onChange={(e) => setPdfLang(e.target.value)}
              sx={{
                fontSize: '0.8rem',
                color: MP.white,
                '.MuiOutlinedInput-notchedOutline': { borderColor: alpha(MP.white, 0.2) },
                '.MuiSvgIcon-root': { color: MP.white },
              }}
            >
              {LANGUAGES.map((l) => (
                <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Save edits to browser storage">
            <Button
              variant="outlined"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{ borderColor: MP.green, color: MP.green, '&:hover': { borderColor: MP.greenLight, bgcolor: alpha(MP.green, 0.1) } }}
            >
              Save
            </Button>
          </Tooltip>

          <Tooltip title="Reset all content to defaults">
            <Button
              variant="text"
              size="small"
              startIcon={<ResetIcon />}
              onClick={handleResetAll}
              sx={{ color: MP.yellow, '&:hover': { bgcolor: alpha(MP.yellow, 0.1) } }}
            >
              Reset
            </Button>
          </Tooltip>

          <Button
            variant="contained"
            size="small"
            startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <PdfIcon />}
            onClick={handleGeneratePDF}
            disabled={generating}
            sx={{
              bgcolor: MP.yellow,
              color: MP.black,
              fontWeight: 800,
              '&:hover': { bgcolor: MP.yellowMid },
              '&:disabled': { bgcolor: alpha(MP.yellow, 0.4) },
            }}
          >
            {generating ? (pdfStatus || 'Generating…') : 'Generate PDF'}
          </Button>
        </Stack>
      </Box>

      {pdfError && (
        <Alert severity="error" onClose={() => setPdfError(null)} sx={{ mx: 3, mt: 2 }}>
          {pdfError}
        </Alert>
      )}

      {/* ── Main content ─────────────────────────────────────────────── */}
      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* EDIT TAB */}
        {activeTab === 0 && (
          <Box>
            <Alert
              severity="info"
              sx={{
                mb: 3,
                bgcolor: MP.cardBg,
                color: MP.textLight,
                border: `1px solid ${alpha(MP.yellow, 0.3)}`,
                '& .MuiAlert-icon': { color: MP.yellow },
              }}
            >
              <strong>Edit Mode</strong> — Modify any text, step, image, or callout below.
              Click <strong>Save</strong> to persist in browser. Switch to <strong>Preview</strong> then <strong>Generate PDF</strong>.
            </Alert>
            <ManualEditor content={content} imageOverrides={imageOverrides} actions={actions} />
          </Box>
        )}

        {/* PREVIEW TAB */}
        {activeTab === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
              <ManualTableOfContents chapters={content.chapters} activeChapter={activeChapter} />
            </Grid>

            <Grid item xs={12} md={9}>
              {/* Cover block */}
              <Box
                sx={{
                  p: 4,
                  mb: 4,
                  background: `linear-gradient(135deg, ${MP.black} 0%, ${MP.deepBlue} 60%, ${MP.blue} 100%)`,
                  borderRadius: 3,
                  borderLeft: `6px solid ${MP.yellow}`,
                  borderBottom: `3px solid ${MP.green}`,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: MP.yellow, letterSpacing: 4, fontWeight: 700, fontSize: '0.7rem' }}
                >
                  SoldiKeeper
                </Typography>
                <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5, mb: 1, color: MP.white }}>
                  {content.meta.title}
                </Typography>
                <Typography sx={{ color: MP.textMuted, mb: 2.5, fontSize: '0.95rem' }}>
                  {content.meta.subtitle}
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                  <Chip
                    label={`Version ${content.meta.version}`}
                    size="small"
                    sx={{ bgcolor: MP.yellow, color: MP.black, fontWeight: 700 }}
                  />
                  <Chip
                    label={content.meta.lastUpdated}
                    size="small"
                    sx={{ bgcolor: alpha(MP.green, 0.9), color: MP.white }}
                  />
                </Stack>
              </Box>

              <Box ref={previewRef} sx={{ bgcolor: MP.offWhite, borderRadius: 3, p: { xs: 2, md: 4 } }}>
                {content.chapters.map((chapter, idx) => (
                  <ManualChapterView
                    key={chapter.id}
                    chapter={chapter}
                    chapterIndex={idx}
                    imageOverrides={imageOverrides}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message={snackMsg}
        ContentProps={{ sx: { bgcolor: MP.green } }}
      />
    </Box>
  );
};

export default UserManual;
